// What happens to work already RUNNING, and the housekeeping around it: the task
// re-enqueue/fail/release/cancel hops, the run-event ingest, session compaction
// and its terminal states, the janitors, the provisioning and config-migration
// PRs they open, retention pruning, the gardener sweep, and the account /
// installation / repository rows the whole thing hangs off.
// Split out of `../audit.ts`, which is now the barrel over this directory. Not in
// the barrel: this is a HALF of `AuditEventKind`, exported only so `./kind` can
// assemble it. Import `AuditEventKind` — never a group.

export const AUDIT_KIND_MAINTENANCE = {
  // #117: the presence reaper reset a reaped runner's in-flight task back to
  // `pending` (same row, ownership cleared) so the assigner re-claims it for a
  // live box. Re-enqueueing lost work is a dispatch state change, so it earns
  // its own audit row — parallel to `TaskAssigned` on the way out. Audit is
  // trust: the trail must show the task moved, and why.
  TaskReenqueued: 'task.reenqueued',
  // Plan 101 runner lifecycle, doc 05 #7: the fleet event sink DROPPED a run event
  // it will never persist — a message it could not decode (`poison`), one it
  // terminated after `EVENT_MAX_DELIVERIES` identical failures (`terminated`), or
  // one whose subject the taxonomy does not produce (`subject_rejected`). Each was a
  // log line only, and a drop that leaves no row is indistinguishable from an event
  // that was never sent. Anchored on the ACCOUNT of the box that published it (the
  // subject proves the box; the box row names the tenant), never on the task's
  // session — the canonical `terminated` case is a task whose rows are gone.
  FleetEventDropped: 'fleet.event_dropped',
  // #4446: the box's own GC report — what the runner-restart hygiene pass found and did,
  // ONE row per box boot, landed by the box-event sink from
  // `fleet.box.<runnerId>.boot_hygiene`. Counts per finding kind and outcome, never a
  // path. Until it the pass wrote one journald line, so census M13 (stray process groups
  // at boot, plan 101 slice 16) had nothing to grade. In this group, not dispatch's with
  // the other `runner.*` rows: it is the box's janitor report, housekeeping like the
  // sink's drop row above.
  RunnerBootHygieneReported: 'runner.boot_hygiene_reported',
  // Plan 101 F7 (#4645): a BullMQ job stalled past `LANE_MAX_JOB_STALLS` (3) and the
  // worker REFUSED it rather than run it a fourth time — a job that keeps killing
  // its worker is not retried into the ground. Until this row the refusal was a
  // failed job that aged out of Redis in an hour and left nothing to count.
  // Platform-chain: the lane, not a tenant, owns it. Input:
  // `{ lane, jobId, stalledCounter, attemptsStarted, repeatJobKey | null }`.
  WorkerJobRefused: 'worker.job_refused',
  // Arc 101 WK-3: a task exhausted its `TASK_MAX_ATTEMPTS` retry budget (or hit
  // a non-retryable failure classification) and was declared TERMINALLY failed.
  // The retry lane's `TaskReenqueued` rows record each attempt going back to the
  // queue; this row is the one that says the platform STOPPED retrying, carrying
  // the failure kind + attempt count — without it a terminal `failed` row has no
  // trail naming why the work was abandoned.
  TaskFailed: 'task.failed',
  // plan 101 runner lifecycle, slice 01: a box GAVE a run back because the box itself
  // was going away (a SIGTERM, a self-update that outran the run ceiling, a lost fleet
  // connection). The task returns to `pending` at the SAME attempt with its fence
  // bumped. Distinct from `TaskReenqueued`, which records an attempt SPENT and re-pended
  // within the retry budget: a restart the task had nothing to do with must be
  // countable as what it is, and must never read as a failure in the trail.
  TaskReleased: 'task.released',
  // #6748 (epic #6552): a run PAUSED by a person stopped at a safe point, saved its work and
  // parked its task `paused` on the same machine, keeping its slot and its repo area. Its own
  // kind, never `TaskReleased`: the release streak bound (#5448) counts `task.released` rows,
  // and a task paused and resumed five times in an hour must never be ended
  // `box_release_loop`. Written by the box's own `run_released{paused}` and by every box
  // release that parks a pause-requested run (drain, lost box, operator drain or revoke).
  TaskPaused: 'task.paused',
  // #6751: the pause DOORS (`apps/api/src/deps/task-pause.ts`). A person asked a RUNNING job
  // to stop at its next step (`pause_requested`: `tasks.pause_requested_at` stamped and the
  // `pause` verb sent to its machine), withdrew that request before the box answered
  // (`pause_withdrawn`), or resumed a paused job (`resumed`: the row back to `pending`, its
  // machine pin kept or, with `anywhere`, cleared). A job not yet started parks at once and
  // writes `task.paused` above. Each row names the token and, when present, the user.
  TaskPauseRequested: 'task.pause_requested',
  TaskPauseWithdrawn: 'task.pause_withdrawn',
  TaskResumed: 'task.resumed',
  // #6768: one item of an accepted move SETTLED — the target's claim landed it (`landed`), or
  // it failed (`source_ended`, `target_released`, `cancelled`). Written in the settle's own
  // transaction: `{ task_id, batch_id, from, to, state, failure }`. A failed item's task is
  // left where the platform left it, never re-moved silently.
  TaskMoved: 'task.moved',
  // A PR closed WITHOUT merging, so its babysit state was retired: the watch
  // anchor plus any in-flight follow-up move `→ cancelled`. The mirror of
  // `TaskReenqueued` — that one records work being given BACK to the queue, this
  // one records work being taken OUT of it — and it earns a row for the same
  // reason: a maintainer whose queued fix silently vanished is owed the sentence
  // saying it was dropped because they closed the pull request. Only a call that
  // actually cancelled something writes one (the CAS is the gate), so a
  // redelivered `closed` webhook is silent.
  TaskCancelled: 'task.cancelled',
  // #4023: an OPERATOR re-drove a terminal (or otherwise stuck) task back to
  // `pending` through the sanctioned trigger (`POST /v1/tasks/:id/redispatch`)
  // — the only path back for a task whose ladder exhausted, since
  // `redispatchTask` (`@developerz/db`) revives a row's status but leaves
  // `attempt`/`failure_kind` exactly where they were: the ladder history IS
  // the audit, so this row is the operator's TRIGGER, not a ladder reset. Its
  // own kind rather than reusing `TaskReenqueued`: that one is the dispatch
  // lane's own automatic recovery (a reaped/cooled box), this one is a human
  // decision on a row the platform had already given up on.
  TaskRedispatched: 'task.redispatched',
  // #2270: an operator / control-plane verb that was filed as `mcp_tool.call.*` with its
  // name in `tool`. The value IS that name, so a new row's `kind` equals the old rows'
  // `tool`. For a name that was NEVER split, one query reads both:
  // `kind = $1 OR (kind LIKE 'mcp_tool.call.%' AND tool = $1)`. A name #6124 split by
  // verdict (an ok/err pair, or `plan.file_refused`) has its own reads, beside it.
  // The operator's cancel / redispatch REFUSED (`deps/mcp-ports.ts`, `plan-writes.ts`,
  // `task-redispatch.ts`, `review-rerun.ts`): the refusals are the rows an operator
  // goes looking for, beside the first-class `task.cancelled` / `task.redispatched`.
  TaskCancelRefused: 'task.cancel_refused',
  TaskRedispatchRefused: 'task.redispatch_refused',
  // An operator added `depends_on` edges to a task (`deps/task-operator.ts`, `box-claims.ts`).
  TaskDependenciesAdded: 'task.dependencies_added',
  // #5299: the `cron.dead_dependency` tick rewrote a pending task's `depends_on` because
  // it named a task a `plan regroup` SUPERSEDED — the edge now names the replacements
  // (`healSupersededDependencies`, `@developerz/db`). The regroup's own `plan.regrouped`
  // row is the decision; this is its follow-through on an edge written after it, and it
  // carries `before`/`after` so the rewrite can be undone.
  TaskDependenciesHealed: 'task.dependencies_healed',
  // #5299 follow-up: the `cron.dead_dependency` tick regrouped a plan's groups whose task
  // a `plan regroup` SUPERSEDED onto the replacements (`healSupersededPlanGroups`,
  // `@developerz/db`) — the case where a task is linked by a second plan (its ask's own)
  // the operator's regroup did not name. `input.regroups` lists each group replaced.
  PlanSupersededRegrouped: 'plan.superseded_regrouped',
  // 2026-09-28: the `cron.dead_dependency` tick removed `depends_on` edges that closed a
  // CYCLE among pending tasks — the edges pointing from an older task to a strictly newer
  // one, which is the edge a superseded-dependency heal wrote before it refused cycles
  // (`breakDependencyCycles`, `@developerz/db`). One row per dependent rewritten, with
  // `before`/`after`/`removed` and the whole `cycle`, so the removal can be undone.
  TaskDependencyCycleBroken: 'task.dependency_cycle_broken',
  // #366: the mothership wrote (or rewrote) a fleet task's lifecycle-narration
  // comment on the GitHub issue/PR it answers — "picked up by <box>", "PR
  // opened", "handed off". An OUTWARD write on the maintainer's repo, so it
  // earns a row on the same terms as `gh.issue_opened` and
  // `stacked_pr.links_written`. Only an APPLIED write writes one (an unchanged
  // body skips the PATCH entirely), so a redelivered stage event is silent.
  TaskNarrated: 'task.narrated',
  // #6082: the mothership DELETED a terminal task's saved branch — cancelled or merged at once,
  // failed once past `FAILED_SAVED_BRANCH_RETAIN_DAYS` (`savedBranchRetireEligibility`) — its own
  // `dz/<slug>--<8hex>` head or legacy `dz/wip/<task id>`, tip a saved commit, no PR on it. An
  // outward write on the maintainer's repo, so one row per delete performed
  // (`TaskSavedBranchRetiredOutputSchema`).
  TaskSavedBranchRetired: 'task.saved_branch_retired',
  // #908: a key-fix (`unverified|invalid → valid`) auto-replayed an entity whose
  // run had died on credentials — one row per re-driven entity + a sweep summary.
  // Audit is trust: an auto-replay of dropped work must leave a trail.
  StalledReplayRedriven: 'stalled_replay.redriven',
  // #118: the mothership event-sink ingested one `fleet.evt.<run_id>` event from
  // a runner and persisted it to `task_run_events` (the per-run hash chain that
  // powers the live terminal). `task_run_events` is cascade-deleted with its
  // task; this audit row is the durable, tamper-evident record that the
  // mothership accepted event X for run Y from an untrusted runner — audit is
  // trust, and it outlives the telemetry it attests to. Parent-less (the agent
  // loop lives on the BYOVM, so there is no mothership `agent_sessions` row).
  TaskRunEventIngested: 'task.run_event',
  // #129: in-session threshold compaction fired — the conversation prefix was
  // summarized (lossy) to keep a huge session inside the model's context
  // window. The row records how much was dropped so the trail shows the model
  // no longer sees the verbatim prefix.
  SessionCompacted: 'session.compacted',
  // arc 101 AC-8: compaction turned ITSELF OFF for the rest of the session —
  // the summarizer failed, so `session.compacted` will never fire again and the
  // context grows unsummarized until the model window refuses it. The absence
  // of further compaction rows is not evidence of a session that never needed
  // one, so the degradation earns a row of its own. Once per session.
  SessionCompactionDisabled: 'session.compaction_disabled',
  // The threshold tripped and compaction had nothing it was ALLOWED to drop:
  // `compactMessages` keeps the last steps verbatim, so a single tool result
  // larger than the window shrinks to nothing and the step goes to the provider
  // at full size. Nothing failed and nothing switched off — distinct from
  // `session.compaction_disabled` on both counts — but without a row of its own
  // the outcome is byte-identical to a session that never needed compacting:
  // prod `run_bf67826b39eb46c49b50b6ca8d0cba0f` reached 4.3x its declared window
  // with zero rows of either kind. One row per offending step, never latched:
  // the next step can genuinely compact once the history outgrows the verbatim
  // tail, and a once-only report would claim a situation resolved that is still
  // burning the window.
  SessionCompactionIneffective: 'session.compaction_ineffective',
  SessionSoftWarn: 'session.cost_soft_warn',
  SessionCompleted: 'session.completed',
  SessionEscalated: 'session.escalated',
  SessionFailed: 'session.failed',
  // 02-3: the terminal of a session the deploy drain cut — its job is handed back for
  // a fresh session. The `handed_back` outcome's row; a roll is not a failure.
  SessionHandedBack: 'session.handed_back',
  // #293: the janitor-cron framework. `janitor.run` brackets one bounded pass of
  // a scheduled maintenance agent — the start row carries the brief (prompt) +
  // scope, the end row the swept counts. `janitor.action` lands once per
  // applied / refused idempotent action (flag-stale, relabel, file-bug,
  // fleet-report). Parent-less jobs (no repo-scoped `agent_sessions` row), so —
  // like the other cron lanes — they are emitted through the worker's structured
  // job-audit log. Never a PR: the janitor write vocabulary has no PR member
  // (see `apps/worker/src/janitors`), so "open a PR" is unrepresentable.
  JanitorRun: 'janitor.run',
  JanitorAction: 'janitor.action',
  // A `janitor.action` whose finding's tenant resolved to NO account (the repo or box
  // row is gone, or the lookup faulted). The action already ran, so it is not
  // dropped and never guessed onto another tenant: it lands on the janitor's
  // PLATFORM chain, the pass's own head, with the action row's input/output plus
  // `tenant` and `reason` (`no_account` | `lookup_failed`). Platform-owned by
  // construction — the one janitor action kind with no account.
  JanitorActionUnattributed: 'janitor.action_unattributed',
  // #447: the onboarding `.mcp.json` provisioner. `mcp_provision.checked` lands
  // once per installed repo the worker inspected (decision: created / merged /
  // unchanged / malformed / skipped-and-why); `mcp_provision.pr_opened` once per
  // disclosed bot PR it opened to wire the managed MCP servers. Two kinds so the
  // trail separates the (idempotent, frequent) check from the (rare) write —
  // audit is trust: every check and every PR action must be on the record.
  McpProvisionChecked: 'mcp_provision.checked',
  McpProvisionPrOpened: 'mcp_provision.pr_opened',
  // #2963: the `.dz/` migration consumer — the half that OPENS the per-repo
  // move PRs (the guard + inventory it composes are #2961). Mirrors the
  // `mcp_provision.*` pair for the same reason: `dz_config_migrate.checked`
  // lands once per installed repo the worker inspected (decision:
  // `pr_opened` / `dry_run` / `unchanged` / `skipped-and-why`); the second
  // row lands ONCE per disclosed bot PR — the (rare) write — and never
  // outside a DRAFT. Two kinds so the trail separates the (idempotent,
  // frequent) check from the (rare) write; audit is trust — every check and
  // every PR action must be on the record. Parent-less (a queue lane, no
  // `agent_sessions` row), so they ride the worker's structured job-audit
  // store beside the provisioner rows above.
  DzConfigMigrateChecked: 'dz_config_migrate.checked',
  DzConfigMigratePrOpened: 'dz_config_migrate.pr_opened',
  // #3149: the `.dz/` migration READY lane — the half that re-runs the #2961
  // guard on a still-open DRAFT move PR and marks it ready for review only
  // when the guard still passes (`apps/worker/src/consumers/
  // dz-config-migrate-ready.ts`). The lane REUSES `dz_config_migrate.checked`
  // for every per-repo check it performs (one vocabulary for "the worker
  // looked at this repo's migration state"), so this kind lands ONCE per PR
  // actually readied — the (rare) write, beside `pr_opened` above. The lane
  // never merges and never arms auto-merge: on developerz.ai the platform App
  // merges a ready PR on green, so readying IS the merge trigger — which is
  // why the guard re-runs at this moment and not only at open time.
  DzConfigMigrateReadied: 'dz_config_migrate.readied',
  // #511 task 3: the maintainer bot edited a fleet slice PR's body to add its
  // stacked-plan links (`Part n/m of plan <id>` + `Depends on #<upstream PR>`).
  StackedPrLinksWritten: 'stacked_pr.links_written',
  // #990: a plan group flipped `→ merged` because the PR its task opened was
  // reported merged by GitHub — the lifecycle transition that unblocks the
  // plan DAG's downstream slices in `claimNextReady`. Parent-less (fired from
  // the worker's PR-projection sink), so it is emitted through the worker's
  // structured job-audit log.
  PlanGroupMerged: 'plan_group.merged',
  // #3800: a plan's group DAG was WRITTEN — the batch `insertPlanGroups` /
  // `insertPlanGroupsIfNewPlan` (`packages/db` `plan-groups/write.ts`) persisted, held
  // or not. Appended through their REQUIRED `record` INSIDE the write's transaction, so
  // the row exists iff the groups do; a fully redelivered batch writes none. `input`
  // names each group's id, repo, edges and status, never an envelope or a title (the
  // one draft: `@developerz/audit` `planRecordedDraft`). Writers, every one on the
  // account's chain through `appendTenantAuditOnTx`: `apps/worker`
  // `wiring/plan-recorded.ts` (the scout ingest, the onboarding scaffold and backfill,
  // `plan execute`), `apps/api` `deps/plan-recorded.ts` (the project create/setup
  // scaffold) and `apps/agents-manager` `wiring/plan-recorded.ts` (the triage lane).
  PlanRecorded: 'plan.recorded',
  // #4018: the plan itself flipped `→ done` because its LAST group merged — the
  // stored, system-written completion of an epic. `output.source` names which of
  // the two writers did it: `pr_merged` (the group flip's own transaction, from
  // the PR-projection sink) or `reconcile` (the `cron.dead_dependency` tick
  // closing a plan that was already all-merged before the writer existed).
  // DURABLE on the account's chain, unlike `plan_group.merged` beside it: the
  // whole point of storing completion is that a query can answer it later.
  PlanDone: 'plan.done',
  // Plan 101 slice 06: a `success` deploy made an upstream leg LIVE where a `deployed` edge
  // waits; one row per leg per release (a repeat success writes none), in the verdict insert's
  // transaction. `input`: `{ planId, groupId, repoFullName, environment, sha, deliveryId }`.
  PlanLegDeployed: 'plan.leg_deployed',
  // Plan 101 slice 07: a leg released its `hold: merge` dependents (`leg-release-wake.ts`).
  PlanLegReleased: 'plan.leg_released',
  // Plan 101 slice 09: a live check's verdict and the shas it proved at settle (`live-verdict-settle.ts`).
  PlanLiveVerdict: 'plan.live_verdict',
  // Plan 101 R3: the plan flipped `→ ended` — every slice settled (failed, cancelled or
  // merged, none superseded) and not every slice landed. Written by the
  // `cron.dead_dependency` tick (`reconcilePlansEnded`); DURABLE on the account's chain
  // beside `plan.done`, for the same reason.
  PlanEnded: 'plan.ended',
  // Plan 101 R3: an `ended` plan went back to `open` because a redispatch revived one of
  // its slices (`reopenRevivedPlans`, in the redispatch's own transaction). Written by
  // the redispatching caller on the account's chain, beside its `task.redispatched` row.
  PlanReopened: 'plan.reopened',
  // #2270 (the note above `TaskCancelRefused`): promoted from `mcp_tool.call.*`.
  // The operator's plan verbs (`deps/plan-writes.ts`): approve (and its refusal / no-op),
  // regroup, unlink, cancel.
  PlanApproved: 'plan.approved',
  PlanApproveRefused: 'plan.approve_refused',
  PlanApprovedNoop: 'plan.approved_noop',
  PlanRegrouped: 'plan.regrouped',
  PlanRegroupRefused: 'plan.regroup_refused',
  PlanUnlinkRefused: 'plan.unlink_refused',
  PlanEdgesUnlinked: 'plan.edges_unlinked',
  PlanCancelled: 'plan.cancelled',
  // #5131: the `plan.mirror` lane filed or updated a GitHub mirror issue on a
  // customer repo — the durable half of the platform's plan-as-issues loop
  // (#5100). `input`/`output` carries `{ planId, part, repoFullName, issueNumber,
  // action }`; `action` is one of `reused | opened | updated | closed` across
  // #5132 (create) and #5133 (keep current). No producer writes this row in
  // #5131: the kind and its `TERMINAL_RENDERERS` entry ship here so the lane
  // can mount the audit arm in #5132 without the project's `kind` enum
  // shifting under any earlier merger.
  PlanMirrored: 'plan.mirrored',
  // #5653: the error-issue lane (`apps/worker/src/consumers/error-issue.ts`)
  // filed one GitHub issue for an error group, beside the `error_issues` ledger
  // row. `input` carries `{ repoFullName, groupKey, rawTaskId }` and `output`
  // carries `{ issueNumber, url }`. The writers are the POST branch in
  // `runClaimedErrorIssuePass` (`error-issue.ts`: the issue it posted, audited
  // whether or not its ledger insert raced) and `ledgerRefoundIssue`
  // (`error-issue-ledger.ts`: an issue a crashed pass left on the repo,
  // audited when its ledger insert lands the row). Rendered 'omit' in
  // TERMINAL_RENDERERS (maintenance-tenancy.ts), as `plan.mirrored` is: the
  // maintainer sees the GitHub issue itself.
  ErrorIssueFiled: 'error_issue.filed',
  // #5653: the same lane CAPPED a group: the per-repo daily issue budget was
  // already spent, so no issue was filed and the `capped` ledger row is the
  // durable record of the decision. `input` carries
  // `{ repoFullName, groupKey, rawTaskId, day }`, `day` being the UTC day the
  // cap counted, and `output` is null. Written by `settleCappedGroup`
  // (`error-issue-ledger.ts`), only when its `capped` ledger insert lands the
  // row. Also written, with `input.reason` (`original_capped`, `original_refused`,
  // `original_unpublished`), by `settleUnpublishedOriginal` (`error-issue-reopen.ts`,
  // #5654) for a recurrence whose original was never published; `reason` stays in
  // `input` as stored since #6398 (#6403). Same lane as `error_issue.filed` beside it,
  // and rendered 'omit' in TERMINAL_RENDERERS likewise.
  ErrorIssueCapped: 'error_issue.capped',
  // #5654: the same lane REOPENED the original public issue of a group whose
  // merged fix was followed by a recurrence (a generation >= 1 candidate), with one
  // disclosed comment naming the fix PR and the generation, instead of filing a new
  // issue. `input` carries `{ repoFullName, groupKey, baseGroupKey, generation,
  // rawTaskId }` and `output` carries `{ issueNumber, url, fixPullRequest }`
  // (`fixPullRequest` is null when the merged task recorded no PR). Written by the
  // reopen pass (`error-issue-reopen.ts`) only when its ledger insert landed the
  // row. Rendered 'omit' like its siblings: the maintainer sees the issue itself.
  ErrorIssueReopened: 'error_issue.reopened',
  // #5654: the reopen pass DECLINED to act on the original issue — it is gone (404 /
  // 410 on the read, the comments read, the comment POST or the state PATCH), moved to
  // another repository, a pull request, `locked` (read off the issue object, before any
  // comment), closed by a maintainer as not planned (or duplicate / with no reason). A 403
  // is NOT one of these: it is the REPO's (archived repos serve reads and refuse writes; a
  // missing issues:write permission answers alike) and is remembered, not recorded here.
  // Nothing public was changed (apart from a comment already posted: `commentPosted`, set
  // when the crash-recovery read found this generation's comment, or a later PATCH was
  // refused). `input` carries `{ repoFullName, groupKey,
  // baseGroupKey, generation, rawTaskId }`; `output` carries `{ reason }` and, for an
  // HTTP refusal, `{ status }`. The generation's ledger row is `capped`. Rendered
  // 'omit', as above.
  ErrorIssueReopenRefused: 'error_issue.reopen_refused',
  // #5657: the error-fix confirmation lane (`cron.error_fix_confirm`, `apps/worker/src/cron/
  // error-fix-confirm.ts`) posted ONE disclosed comment on a group's already-closed public
  // issue: a successful deployment of the fix was reported and no recurrence was recorded for N
  // hours (a success verdict is a report, not proof production changed). It confirms and never
  // closes. `input` carries `{ repoFullName, groupKey, baseGroupKey, generation,
  // rawTaskId }` and `output` carries `{ issueNumber, url, fixPullRequests, deployedAt,
  // windowHours }`. Written only when the `error_issue_confirmations` row THIS write landed
  // (a lost insert race writes no second audit row). Rendered 'omit': the maintainer sees the
  // GitHub comment itself.
  ErrorIssueFixConfirmed: 'error_issue.fix_confirmed',
  // #5657: the same lane DECLINED to confirm a generation. `input` as above; `output`
  // carries `{ reason }` and, for an HTTP refusal, `{ status }`; `commentPosted: true` when
  // the crash-recovery read found a comment of this generation already on the issue before a
  // state refusal. Reasons: `unrecognised_key`, `recurred`, `issue_open`, `locked`, `pull_request`,
  // `repository_mismatch`, `not_planned`, `duplicate`, `no_state_reason`, `issue_gone`.
  // The generation's `error_issue_confirmations` row is `declined`. Rendered 'omit'.
  ErrorIssueFixConfirmRefused: 'error_issue.fix_confirm_refused',
  // `cron.error_issue_settle`: the lane closed a group's public issue because every task of the
  // generation settled with no merged fix and no open pull request. Input `{ repoFullName,
  // groupKey, baseGroupKey, generation, rawTaskId }`, output `{ issueNumber, url, outcome }`.
  // Written only when the `error_issue_settlements` row THIS write landed.
  ErrorIssueSettledClosed: 'error_issue.settled_closed',
  // The same lane decided it will never close this generation's issue, with the reason. Same
  // input; output `{ reason }`. The `error_issue_settlements` row is `declined`.
  ErrorIssueSettleDeclined: 'error_issue.settle_declined',
  // #5658: the error-fix RELEASE lane (`cron.error_fix_release`, `apps/worker/src/cron/
  // error-fix-release.ts`) posted ONE disclosed "Shipped in <tag>" comment on a group's public
  // issue: the first stable GitHub release that contains every merged fix of the generation. It
  // announces and never closes, reopens, edits or labels. `input` carries `{ repoFullName,
  // groupKey, baseGroupKey, generation, rawTaskId }` and `output` carries `{ issueNumber, url,
  // fixPullRequests, releaseTag, releasePublishedAt, staleLiveTasks }` (`staleLiveTasks`: how many
  // otherwise-live tasks of the generation had no status change in 14 days and so did not hold
  // the announcement back). Written BEFORE the `error_issue_release_notes` row, so a process that
  // dies between the two leaves no row and the next tick audits again (an accepted duplicate); a
  // pass that loses the insert race has already written its audit, and the `error_fix_release_race`
  // log names the winner. Rendered 'omit': the maintainer sees the GitHub comment itself.
  ErrorIssueFixReleased: 'error_issue.fix_released',
  // #5658: the same lane DECLINED to announce a release on a generation's issue. `input` as
  // above; `output` carries `{ reason }` and, for an HTTP refusal, `{ status }`;
  // `commentPosted: true` when the crash-recovery read found a comment of this generation
  // already on the issue before a state refusal. Reasons: `locked`, `pull_request`,
  // `repository_mismatch`, `not_planned`, `duplicate`, `no_state_reason`, `issue_gone`,
  // `compare_unresolvable` (a fix sha 404ed against only SOME compares of one aged release over
  // repeated sightings: that release is not proven to contain every fix, not proven to lack one).
  // A `compare_unresolvable` refusal is a FORCED decline and carries six MORE fields beside the
  // reason: `{ repoFullName, groupKey, tag, notFound, shaCount, firstSeenAt }` — the release that
  // stayed unresolved, how many of its fix shas 404ed, and when the FIRST partial-404 sighting
  // was (the instant the decline is measured from). The generation's `error_issue_release_notes`
  // row is `declined`. Written BEFORE that row, like
  // `ErrorIssueFixReleased`: a pass that loses the insert race has already audited its refusal.
  // Rendered 'omit'.
  ErrorIssueFixReleaseRefused: 'error_issue.fix_release_refused',
  // #5659: `cron.error_fix_timing` posted ONE timing-only comment on a NEW error's own issue (first
  // seen shortly after a fix's reported deploy). 'omit': the maintainer sees the comment itself.
  ErrorIssueFixTimingNoted: 'error_issue.fix_timing_noted',
  // #5659: the same lane declined (reasons as `fix_release_refused`). Rendered 'omit'.
  ErrorIssueFixTimingRefused: 'error_issue.fix_timing_refused',
  // #1253: a SELF-MERGE was recorded — the PR's author and its merger are the
  // same GitHub login. NEUTRAL by design: the row presumes no verdict on
  // legitimacy — no `violation`, no `sanctioned`, no `allowed` field, no
  // severity, no escalation. It records; something else may later judge (the
  // sanction question stays on parent #1205). Fired from the worker's always-on
  // PR-projection sink on the one action that reports a merge (`closed` +
  // `merged: true`), which covers babysit auto-merge AND a human merge.
  // Parent-less, so it rides the worker's structured job-audit store like
  // `plan_group.merged` beside it.
  PrSelfMerged: 'pr.self_merged',
  // #1787: a HUMAN-authored PR was linked to the issue it answers — the durable
  // half of the reporter-facing loop, whose accept path ("open a PR whenever you
  // are ready") previously ended at a sentence nothing followed. `output` carries
  // `{repo_full_name, pr_number, issue_number, source}`, where `source` names
  // which of the two link readings found it (`graphql` = GitHub's own
  // `closingIssuesReferences`, `body_keyword` = the body's closing keywords).
  // NOT parent-less: a human-authored PR mints no agent session, so the row is
  // anchored on a gateway-style account parent (`ensureGatewaySessionParent`, the
  // #823/#973 pattern) and lands in `audit_events` rather than the stdout job
  // log — the link is the deliverable here, so it has to be queryable.
  PrIssueLinked: 'pr.issue_linked',
  // The babysit split follow-through (`apps/worker/src/babysit/split-followthrough.ts`)
  // made an OUTWARD write on OUR bot's non-converging PR once triage cut its split:
  // `output.action` is `closed` (closed + the marker comment posted), `comment_posted`
  // (a close whose comment had not landed) or `comment_updated` (the marker comment
  // edited to link a split PR that opened). Only an APPLIED write writes one. The
  // supersede itself — cancel + regroup — is its own `task.cancelled` row, written
  // before any GitHub call.
  PrSplitRetired: 'pr.split_retired',
  // #2270 (the note above `TaskCancelRefused`): promoted from `mcp_tool.call.*`.
  // An operator re-armed a PR's babysit (`deps/pr-babysit-rearm.ts`). Its sibling
  // `pr.pending_decided` is NOT here: it is a stdout belt line beside the durable
  // `tool.call.ok` / `action.redeem` row (`deps/pr-decide-pending.ts`), never an
  // account-audit row, so there was nothing to promote.
  PrBabysitRearmed: 'pr.babysit_rearmed',
  /**
   * #3702/#4283: `scripts/backfill-merge-commit-sha.ts --confirm` overwrote a
   * `pull_requests.merge_commit_sha` that held GitHub's ephemeral mergeability
   * PROBE (written before #4283 gated the read on the merged payload) with the
   * real merge commit GitHub now reports for that pull request. `input` carries
   * `{repoId, githubNumber}`; `output` carries `{stored, real}` — the probe and
   * the commit written in its place. One row per repaired candidate, appended in
   * the SAME transaction as the `pull_requests` UPDATE: a failing append rolls
   * the write back with it, so a repaired row is never unlogged. On the chain of
   * the account that owns the repo (deep sweep 01-15).
   */
  PrMergeCommitShaRepaired: 'pr.merge_commit_sha_repaired',
  /**
   * #3935: `scripts/backfill-pr-merged-at.ts --confirm` filled the merge timestamps
   * GitHub reports onto a `merged` `pull_requests` row written before #3901 clocked
   * them (`merged_at` was NULL). `input` carries `{repoId, githubNumber}`; `output`
   * carries GitHub's `{mergedAt, closedAt, openedAt, mergeCommitSha}` as read. Every
   * column but `merged_at` was filled only where it was NULL. One row per filled
   * candidate, appended in the SAME transaction as the UPDATE, so a filled row is
   * never unlogged. On the chain of the account that owns the repo (deep sweep 01-15).
   */
  PrMergeTimestampsBackfilled: 'pr.merge_timestamps_backfilled',
  // Plan 101 rows 07-2 / 09-1: the PR-projection sink REFUSED to link a pull request to
  // the task its `dz-task` marker named. Nothing was written to the task, and a write
  // that did not happen leaves no other row, so the refusal is its own. `input` carries
  // `{accountId, repo, prNumber, taskId}`; `output` carries `{linked: false, reason}`
  // plus what that refusal knows. WHICH refusals exist, and what each adds to `output`,
  // is the writer's to enumerate and is not restated here: `reason` is a member of
  // `MarkerLinkRefusal['kind']` (`apps/worker/src/babysit/marker-link.ts`), and the row
  // is built by `refusedMarkerLinkAuditEvent` (`apps/worker/src/wiring/
  // pr-projection-sink.ts`). Visible to the maintainer: a pull request claimed one of
  // their tasks.
  TaskPrLinkRefused: 'task.pr_link_refused',
  // #448: the 72h raw-log retention prune redacted (or failed to redact)
  // `task_run_events` payloads. Mutating retained event content is a
  // security-relevant state change, so every pass — success AND failure —
  // gets a row beyond the stdout metric line. Parent-less (cron lane), so it
  // is emitted through the worker's structured job-audit log.
  LogRetentionPruned: 'log_retention.pruned',
  // #3235: the two DAILY retention lanes that bound the platform's highest-volume
  // tables closed a pass — `cron.prune_inbound_events` and `cron.prune_outbox`.
  // Both destroy rows and both recorded the pass only on stdout, so neither kind
  // existed and nothing could ever have written one; these are the members that
  // make the lanes auditable, not a repair of a mis-binding.
  //
  // `inbound_events.pruned` names the raw webhook bodies dropped past
  // `INBOUND_EVENTS_RETENTION_DAYS` — the canonical payloads every dispatch lane
  // rehydrates from, and the replay horizon `apps/ingest`'s delivery nonce is
  // pinned to, so "what did we still hold on the day of that incident" is a
  // question only this row can answer. `outbox.pruned` names the published fleet
  // dispatches dropped past `OUTBOX_RETENTION_DAYS` — the forensic record of what
  // the mothership pushed — and carries the same pass's `fleet_run_audit_outbox`
  // obligation count, which is an AUDIT HOLE probe: a chain that wedged and healed
  // otherwise leaves nothing behind saying it ever wedged.
  //
  // ONE row per pass, on every outcome, for the reason `RunnerReleasePruned`
  // states: absence is not evidence, and a MISSING row must read as "the schedule
  // stopped" rather than "nothing aged out". (`RunnerReleasePruned` itself is one
  // CLOSING row per pass plus, on a pass that sets `runner_releases.pruned_at`, one
  // `outcome: 'marked'` row written before that one-way write, #7213; a per-tick
  // count of it filters `outcome <> 'marked'`.) Platform-anchored (a cron lane owns no
  // session), chained on the lane's own `agent_sessions` parent — not the
  // parent-less job-audit store, which only reaches stdout.
  InboundEventsPruned: 'inbound_events.pruned',
  OutboxPruned: 'outbox.pruned',
  // plan 101 09 rows 3-5: `cron.prune_platform_tables` closed a daily pass over
  // `webhook_deliveries`, dead `sessions`, `notifications` and delivered
  // `digest_queue` entries. ONE row per pass on every outcome, carrying each
  // table's window, deleted count and whether the per-run bound deferred the
  // rest — the rows are gone afterwards, so this is the only evidence of what
  // the platform dropped and when. Platform-anchored like the two above.
  PlatformTablesPruned: 'platform_tables.pruned',
  // #4400 (epic #4391, Terminal slice 09): the third of the issue's three named
  // emissions. Source and event type only, NEVER the payload — the payload
  // stays in `inbound_events`, exactly like `InboundEventsPruned` above never
  // carries the rows it deletes. Fires from `apps/ingest/src/routes/hooks.ts`'s
  // `POST /hooks/:source_id` lane, right after the connection row resolves the
  // tenant and the delivery is persisted as genuinely NEW (the same
  // `onConflictDoNothing` insert `isReplay` already gates on) — never on a
  // redelivered `delivery_id`, which is the transition this kind names.
  // NOT parent-less: like `PrIssueLinked` above, a webhook has no agent
  // session, so the row chains onto the account's `ensureGatewaySessionParent`
  // anchor via `@developerz/audit`'s `createTenantAuditSink`.
  //
  // Scoped to `/hooks/:source_id` ONLY — every connected non-GitHub source
  // (Sentry, Linear, generic), which is the one lane that resolves an account
  // AT RECEIPT (`source.accountId`, the connection row IS the tenant).
  // `/gh/webhooks` (`apps/ingest/src/index.ts`) has no such seam: a GitHub
  // delivery's tenant is decided downstream, per event kind, across several
  // consumer lanes, not at one hop this pod owns — instrumenting it is a
  // separate, larger change the issue does not describe and this kind does not
  // cover.
  InboundReceived: 'inbound.received',
  // #456: the weekly gardener — the automatic drift/update sweep over managed
  // repos + enrolled boxes. `gardener.sweep_started` brackets the tick's start;
  // `gardener.sweep_summary` closes it with the counts (repos checked / enqueued /
  // deferred-by-cap, boxes tasked / skipped). Per-repo and per-box actions land
  // through the REUSED lanes' own kinds (`mcp_provision.*`, `task.assigned`), so
  // these two only attest the sweep itself — a tick where nothing drifted is a
  // no-op beyond exactly this pair of rows. Parent-less (a cron, no session), so
  // they are emitted through the worker's structured job-audit log.
  GardenerSweepStarted: 'gardener.sweep_started',
  GardenerSweepSummary: 'gardener.sweep_summary',
  // arc 101 slice 03: the onboarding worker lane — the ONLY thing that
  // provisions tenant rows today (the GitHub App install lifecycle). Every
  // tenant-state write it makes earns a row (audit is trust — this is the lane
  // that *creates the tenant*): `account.provisioned` when the owning account is
  // upserted, `installation.linked` / `installation.revoked` when the
  // install↔account link is recorded / torn down (uninstall / suspend), and
  // `repository.discovered` / `repository.disabled` per repo added / removed.
  // `repository.enabled` has no writer since #7802 (a person's enable is `project.enabled`);
  // its rows stay readable on every chain that holds them.
  // On the account's own gateway chain: `account.provisioned` inside the identity
  // transaction (#3841), the rest through the worker's tenant sink (`DispatchAudit`,
  // #2562), which alerts and falls to its stdout belt when a durable append fails.
  // `installation.revoked` files on the account the revoke's UPDATE returns; a
  // teardown matching no `installations` row has no account and alerts instead.
  // Before #2562 all but `account.provisioned` reached only stdout.
  AccountProvisioned: 'account.provisioned',
  /**
   * #3841: an account identity write (`upsertAccountByGithubId`) found a login held
   * by a row carrying a DIFFERENT GitHub account id — the login was renamed away
   * and re-registered by someone else — and released it on the stale row to
   * `login~<id>`. Its own kind, not a field on `account.provisioned` alone: the row
   * released is a different tenant from the one provisioned, and "which tenant lost
   * its login, and why" is the question an operator asks after a rename.
   */
  AccountLoginReleased: 'account.login_released',
  /**
   * One human's DUPLICATE `users` rows folded onto the row they actually sign in
   * as (#4483), by `scripts/consolidate-duplicate-user-rows.ts`.
   *
   * Its own kind because the subject is an IDENTITY, not an account: the rows
   * arose from migration 0038's synthetic ids and an SSO link that landed on the
   * wrong row, and "which row survived, what it absorbed, and what was retired"
   * is the question asked afterwards. These rows are deleted and nothing else
   * records they existed, so the payload reconstructs each one. Its keys, exactly:
   *
   * - `input`: `{ login, keep, retire }` — the login ruled on, the surviving row's
   *   id, and the retired rows' ids.
   * - `output`: `{ adoptedEmail, stampedGithubUserId, retired }` — the email the
   *   survivor took (or `null`), the GitHub id stamped onto it (or `null`), and one
   *   entry per retired row: `{ id, login, email, githubUserId, hasSsoSubject,
   *   approvedAt, cascaded, memberships }`. `githubUserId` includes a synthetic id;
   *   `approvedAt` is an ISO string or `null`; `memberships` is the
   *   `{ accountId, role }[]` its DELETE returned; `cascaded` is the COUNT of rows
   *   under each cascading foreign key other than memberships, keyed `table.column`,
   *   a key absent where there were none. Counts, not contents: a cascaded session
   *   or notification is recorded as having existed, not restored.
   *
   * Every email lands as `[REDACTED:email]` (or `null` where there was none): the
   * audit store scrubs payload strings (`docs/idea/audit.md` § Redaction), so the
   * row records THAT a retired row had an address, not which. An adopted address
   * lives on in the survivor's `users.email`.
   */
  UserRowsConsolidated: 'user.rows_consolidated',
  AccountLoginRenamed: 'account.login_renamed',
  /**
   * #3841: an account identity write REFUSED — the login is held by a row with no
   * `github_account_id` yet, so nothing proves whether the caller is that tenant or
   * a re-registration of its old login. Nothing was written; the backfill
   * (`scripts/backfill-account-github-ids.ts`) is the remedy, and the kind (with its
   * branch) is deleted once the column goes NOT NULL.
   */
  AccountIdentityRefused: 'account.identity_refused',
  /**
   * #3841: `scripts/backfill-account-github-ids.ts --apply` stamped a NULL
   * `accounts.github_account_id` from App-authenticated installation evidence (every
   * live installation agreeing on one id), never from a login lookup. One row per
   * successful compare-and-swap, on the stamped account's own gateway chain,
   * carrying the installations and ids seen — stamping a tenant's identity key is the
   * write a takeover would make, so it is never an unlogged one.
   */
  AccountGithubIdBackfilled: 'account.github_id_backfilled',
  /**
   * #3841: `scripts/seed-org-sso.ts` (`seedOrgIdentityProvider`) enabled custom SSO
   * on an org and created or replaced its IdP connection — an authentication trust
   * anchor. Written in the SAME transaction as the write, on the platform chain
   * `seed:org-sso`, naming the account by its GitHub id; the sealed config is never
   * in the row.
   */
  AccountOrgIdpSeeded: 'account.org_idp_seeded',
  /**
   * Plan 102 slice 06 (#7545): an owner or admin set the account's maintenance
   * posture (`accounts.maintain_posture`, `standard` | `maintain`) through the one
   * audited route. Appended on the SAME transaction as the write
   * (`setAccountPosture`, `@developerz/db`), one row per set — a no-op set
   * included, so "who asked and when" stays answerable. `output` is
   * `{ before, after }` as that write answers it.
   */
  AccountPostureChanged: 'account.posture_changed',
  InstallationLinked: 'installation.linked',
  InstallationRevoked: 'installation.revoked',
  /**
   * Plan 101 slice 03 door 3 (#7298): the App PERMISSION MAP stored on an installation
   * (`installations.permissions`) MOVED. Two observers write the column through the one
   * `recordInstallationPermissions` query — the `installation` webhook's `created` /
   * `new_permissions_accepted` arms (`apps/worker/src/consumers/onboarding.ts`) and the
   * reconcile's `GET /app/installations/{id}` read
   * (`apps/worker/src/consumers/installation-reconcile.ts`) — and both audit through
   * `observeInstallationPermissions` (`apps/worker/src/consumers/installation-permissions.ts`),
   * so this row has ONE spelling. Written only when the write's own pre-image differs
   * (`changed` is decided in SQL, `RETURNING old.*`), so a redelivery or a converged
   * sweep audits nothing; a FIRST observation carries `before: null`. `input` is
   * `{ installationId, accountId, source, deliveryId? }` (`source`: `installation_webhook`
   * | `app_read`); `output` is `{ before, after }`, the two maps VERBATIM (GitHub's slugs
   * as keys), so the move can be read back without GitHub. Tenant-anchored on the
   * installation's account and `visibleToMaintainer`: the `repo_ci_gate` rung reads
   * `workflows: write` off this column, and the owner's click that grants it is the
   * transition this row records.
   */
  InstallationPermissionsChanged: 'installation.permissions_changed',
  RepositoryEnabled: 'repository.enabled',
  /**
   * The account stopped operating a repo, on that account's chain. `input.source` names
   * the writer (#5962): `installation_removed`, `git_token_self_heal`, `ops_orphan_sweep`
   * or `transferred_out` (another account took the row: the install upsert or the
   * operator's light switch, #6248). Every source carries `cancelledTaskIds`; the
   * account's own switch-off is `project.disabled`, not this kind.
   *
   * A `transferred_out` row's `input` is `{ githubRepoId, fullName, deliveryId,
   * source, cancelledTaskIds, purgedSecrets }` (`transferredOutAuditOnTx`,
   * `apps/worker/src/consumers/onboarding-repo-leave.ts`): `fullName` is the LEAVING
   * account's name for the repo, `deliveryId` is `null` from the light switch, and
   * `purgedSecrets` is `{ credentialVault, projectIngress, repoSecrets }`, the COUNT of the
   * leaving account's rows the move deleted, never a name or a value. `purgedSecrets` is
   * an ADDITIVE key from #3843: a `transferred_out` row written before it has none, which
   * means "not recorded", not zero. No reader keys on it, and the row hash covers the
   * stored `input` as written, so older rows still verify.
   */
  RepositoryDisabled: 'repository.disabled',
  // #2270 (the note above `TaskCancelRefused`): promoted from `mcp_tool.call.*`.
  // The account's own project switch (`deps/repos.ts`, `deps/project-control.ts`),
  // distinct from the installation-driven `repository.disabled` above.
  ProjectEnabled: 'project.enabled',
  ProjectDisabled: 'project.disabled',
  // A person took a recorded approve or skip back (`apps/api/src/deps/pr-undo-decision.ts`).
  PrDecisionUndone: 'pr.decision_undone',
  ProjectEnableRefused: 'project.enable_refused',
  // #6124 (the #2270 note above `TaskCancelRefused`): the account sites #2270 left on
  // `mcp_tool.call.*`, each value the name the site wrote in `tool`. A name that was
  // written as BOTH ok and err got an err sibling, so the kind carries the verdict:
  // `admin.provision` / `admin.provision_partial`, `repo_binding.user_token_bind` /
  // `repo_binding.user_token_bind_refused`. The #2270 note's one query is WRONG for
  // these: old err rows carry the BASE name in `tool`. History reads:
  //   base:    kind = <base> OR (kind = 'mcp_tool.call.ok' AND tool = <base>)
  //   sibling: kind = <sibling> OR (kind = 'mcp_tool.call.err' AND tool = <base>)
  // The project trails (`deps/greenfield.ts`, `deps/project-setup.ts`): a greenfield
  // project created, or its record / scaffold dispatch / scaffold plan failed; and an
  // existing repo's setup plan handed to dispatch, or not planned.
  ProjectCreated: 'project.created',
  ProjectRecordFailed: 'project.record_failed',
  ProjectScaffoldDispatchFailed: 'project.scaffold_dispatch_failed',
  ProjectScaffoldPlanFailed: 'project.scaffold_plan_failed',
  ProjectSetupDispatched: 'project.setup_dispatched',
  ProjectSetupPlanFailed: 'project.setup_plan_failed',
  // A repo's canary switch (`deps/repos.ts`), and a user-token repo binding made,
  // refused or removed (`deps/user-token-binding.ts`).
  RepoCanaryEnabled: 'repo.canary_enabled',
  RepoCanaryDisabled: 'repo.canary_disabled',
  // A repo's public-stats consent set or withdrawn (`deps/repos.ts`, #5660).
  RepoPublicStatsEnabled: 'repo.public_stats_enabled',
  RepoPublicStatsDisabled: 'repo.public_stats_disabled',
  // A repo's platform-side merge hold set or cleared (`deps/repo-merge-override.ts`, #7011),
  // and a clear refused because the file itself does not auto-merge (409).
  RepoMergeOverrideSet: 'repo.merge_override_set',
  RepoMergeOverrideRefused: 'repo.merge_override_refused',
  // A squad (#7010, `apps/api/src/deps/squads.ts`): an admin-named group of repos created,
  // renamed, re-membered or deleted, each in the write's own transaction; and a write
  // refused with a 409 / 422 (`squad_name_taken`, `repo_in_another_squad`,
  // `squad_name_invalid`), which changed nothing.
  SquadCreated: 'squad.created',
  SquadRenamed: 'squad.renamed',
  SquadReposSet: 'squad.repos_set',
  SquadDeleted: 'squad.deleted',
  SquadWriteRefused: 'squad.write_refused',
  RepoBindingUserTokenBind: 'repo_binding.user_token_bind',
  RepoBindingUserTokenBindRefused: 'repo_binding.user_token_bind_refused',
  RepoBindingUserTokenUnbind: 'repo_binding.user_token_unbind',
  // The onboarding funnel: a step advanced (the api's `index.ts` and the worker's
  // `consumers/onboarding-advance-port.ts`, one spelling), and the plan-intent pair (#1042).
  OnboardingAdvanced: 'onboarding.advanced',
  OnboardingIntentPersisted: 'onboarding.intent_persisted',
  OnboardingIntentPersistFailed: 'onboarding.intent_persist_failed',
  // Sign-in and identity: a session minted (`deps/session-mint.ts`); the SSO link trail
  // (`deps/sso-login.ts`, `deps/sso-identity-audit.ts`: a subject linked, a link refused
  // fail-closed on a collision, a role raised, a login refused for foreign memberships);
  // an org's SSO connection set or removed (`deps/org-sso.ts`); a personal GitHub token
  // connected or cleared (`routes/me-github-token.ts`); `/v1/me`'s account switch and
  // ToS acceptance (`routes/me.ts`); the declared region and the separate cross-border
  // consent granted or withdrawn (`routes/me-consents.ts`, #5881).
  AuthLogin: 'auth.login',
  // A sign-in by the human already signed in on the browser WIDENED that session
  // (#466): an org's pin added, or GitHub unpinning it (`deps/session-mint.ts`,
  // the link callback). Ids only: the session, the human, the pin or `unpinned`.
  AuthSessionReachExtended: 'auth.session_reach_extended',
  SsoUserLinked: 'sso.user_linked',
  SsoLinkCollision: 'sso.link_collision',
  SsoRoleElevated: 'sso.role_elevated',
  SsoLinkRefusedForeignMemberships: 'sso.link_refused_foreign_memberships',
  // One user, many logins (#466): a login JIT-created its user; a session's user
  // re-linked an identity it already held; proof-of-both merged another user in;
  // an identity was unlinked (`@developerz/db` `IdentityAuditEvent`, written inside
  // the identity transaction by `deps/sso-identity-audit.ts`).
  SsoUserCreated: 'sso.user_created',
  SsoIdentityRelinked: 'sso.identity_relinked',
  SsoUsersMerged: 'sso.users_merged',
  SsoIdentityUnlinked: 'sso.identity_unlinked',
  // One human, two users, bound by EMAIL PROOF (#7426): the signed-in user asked for a
  // link to another user with the same verified email (a mail went out); the confirm
  // merged the other user in; or the confirm refused, decided inside the merge's
  // transaction (`@developerz/db` `mergeUsersByEmailProof`, `routes/me-link-requests.ts`).
  IdentityLinkRequested: 'identity.link_requested',
  IdentityUsersMerged: 'identity.users_merged',
  IdentityLinkRefused: 'identity.link_refused',
  OrgSsoSet: 'org_sso.set',
  OrgSsoRemoved: 'org_sso.removed',
  OrgSsoRemovedNoop: 'org_sso.removed_noop',
  GithubTokenConnected: 'github_token.connected',
  GithubTokenDisconnected: 'github_token.disconnected',
  AccountActiveAccountSwitch: 'account.active_account_switch',
  UserTosAccepted: 'user.tos_accepted',
  UserRegionSet: 'user.region_set',
  UserConsentGranted: 'user.consent_granted',
  UserConsentWithdrawn: 'user.consent_withdrawn',
  // Staff on a user (`routes/admin-invitations.ts`): approved or suspended; and the
  // one-call provision (`deps/provision.ts`), whole or partial (a step refused).
  UserApproved: 'user.approved',
  UserSuspended: 'user.suspended',
  AdminProvision: 'admin.provision',
  AdminProvisionPartial: 'admin.provision_partial',
  /**
   * An install delivery or the installation reconcile (#2540, #7802) created a
   * `repositories` row for a repo we had never seen — DORMANT (`enabled = false`), so it is neither
   * `repository.enabled` (nothing was turned on) nor `repo.requires_upgrade`
   * (no tier question was asked). A repo that predates the App install gets no
   * webhook that could mint its row, so this is the only audit row that will
   * ever attest its arrival, and it must not claim the bot started operating
   * it: enabling stays an explicit human act through `PATCH /v1/repos/:o/:r`,
   * which raises `project.enabled` at that moment.
   */
  RepositoryDiscovered: 'repository.discovered',
  /**
   * The installation reconcile (#2540) listed a repo it could NOT create a row
   * for: `repositories_forge_repo_id_idx` is keyed on `(forge, instance, github_repo_id)`,
   * so the id already belongs to another account's `(forge, instance)` pair and
   * `ON CONFLICT DO NOTHING` skipped it. The installation covers the repo and this account
   * will never see it.
   *
   * Its own kind, not a field on `repository.discovered`: nothing was
   * discovered, and filing a conflict under a creation would make both
   * unqueryable. Detection only — deciding which account owns a repo is not
   * that lane's call, so the row exists to make "has this ever happened?"
   * answerable rather than to authorize a repair.
   */
  RepositoryClaimConflict: 'repository.claim_conflict',
  /**
   * The repo was marked AI-FIRST (`repositories.ai_first_at`), which is what
   * releases the claim gate's hold on every `outcome_kind = 'pr'` task on it
   * (`docs/idea/repo-onboarding.md`). Three writers reach this row — a setup
   * pass's `ready` verdict, the `dz/onboarding` pull request merging, and a
   * maintainer declaring it over `PATCH /v1/repos/:o/:r` — so the row is the one
   * place that answers "who decided this repo was ready, and when".
   *
   * Written only when the stamp ACTUALLY landed: the marker is first-stamp-wins,
   * and a redelivery re-announcing a transition it did not cause is exactly the
   * lie an audit trail must not carry.
   */
  RepoAiFirstMarked: 'repo.ai_first_marked',
  // arc 101 slice 01: `repositories.private` is an ENTITLEMENT fact (the free tier
  // covers your own PUBLIC repos), so a move of it is a billing-relevant state
  // change, not a cosmetic one — and the truth lives on GitHub, so only a delivery
  // or a re-sync can tell us it moved.
  //   `repo.visibility_changed` — one row per ACTUAL flip. Both writers (the
  //     `repository` `publicized`/`privatized` webhook and the installation
  //     visibility backfill) go through the same CAS, so a redelivery or a re-sync
  //     that changes nothing audits nothing, and the row carries which lane moved it.
  //   `repo.requires_upgrade` — the flip took an ENABLED repo private. We
  //     deliberately do NOT auto-disable (destructive, and the maintainer may be
  //     mid-upgrade) and in-flight work finishes, so nothing else in the system
  //     records that the repo's entitlement question just changed: this row plus its
  //     notification ARE the trail. Whether that private repo may still draw NEW work
  //     is the dispatch gate's call (tier × visibility), not this row's.
  // Durable on the repo's account gateway chain: both visibility writers through
  // `stampRepositoryVisibility`'s store + head port (the webhook's `lifecycleAudit`,
  // the backfill's `anchored` arm), the install path's private-repo refusal through
  // the worker's tenant sink (`DispatchAudit`, #2562).
  RepoVisibilityChanged: 'repo.visibility_changed',
  RepoRequiresUpgrade: 'repo.requires_upgrade',
  // #5647: two or more of the repo's App-authored pull requests were reverted inside
  // 24 h, and the platform auto-paused its own worker mode on the repo. At most one
  // row per trip (none if every append attempt fails), written by `prProjectionSink.pauseOnRevert`; a webhook redelivery of
  // the same revert writes nothing (the `reverted_by_pr_number` guard).
  AgentModeAutoPaused: 'agent_mode.auto_paused',
  // plan 101 slice 06 I5: GitHub renamed or transferred the repo, and
  // `repositories.full_name` followed it (keyed on the immutable
  // `github_repo_id`). Every resolver keys on `full_name`, so a rename that never
  // reached the DB left the repo looking unknown — policy unresolved, repo
  // disabled. One row per ACTUAL rename (a CAS: a redelivery writes nothing),
  // from `repository.renamed|transferred` or an `installation_target` org rename.
  RepoRenamed: 'repo.renamed',
  // …and the renames it REFUSED: a `repository.transferred` (the repo left this
  // tenant's account, so renaming it in place would hand one tenant's row another
  // owner's name) and an `installation_target.renamed` for an installation we hold
  // no live account for (it must not mint an `accounts` row). Platform-anchored:
  // the second case has no tenant to anchor on.
  RepoRenameRefused: 'repo.rename_refused',
  // plan 101 slice 06 I1/I2: a webhook delivery that never became work is now
  // either redriven or on the record.
  //   `inbound.terminally_failed` — a `gh.*` dispatch job spent its whole retry
  //     ladder; the `inbound_events` row is marked so `cron.inbound_redrive`
  //     re-enqueues it. Before this, the job was lost after ~90 s with no trace.
  //   `inbound.redriven` — the redrive cron re-enqueued a terminally failed row
  //     (after winning the row's CAS, so a concurrent pass never doubles it).
  //   `inbound.redelivery_requested` — the redrive cron asked GitHub to redeliver
  //     a delivery GitHub recorded as failed (5xx / timeout) that never became a
  //     processed `inbound_events` row. GitHub does not retry on its own.
  // Platform-anchored (the lane owns no session; the row is about a DELIVERY).
  InboundTerminallyFailed: 'inbound.terminally_failed',
  InboundRedriven: 'inbound.redriven',
  InboundRedeliveryRequested: 'inbound.redelivery_requested',
  // plan 101 10-12: `inbound.orphaned` — the redrive cron found a row the dispatch
  // lane was handed (`dispatch_lane`) that no job holds any more and nothing
  // settled: the queue state behind it was lost (a Dragonfly wipe or a snapshot
  // rewind). The row is marked failed so the redrive ladder recovers it.
  InboundOrphaned: 'inbound.orphaned',
  // plan 101 slice 06 I8: a best-effort dispatch sink (projection, babysit,
  // command, reply, link, ack, ...) threw inside `handleGhJob`. The job still
  // completes — one sink's fault must not strand the maintainer lane — but the
  // fault is no longer a `console.error` alone: this row is what tells "the lane
  // broke" apart from "the webhook never arrived".
  DispatchSinkFaulted: 'dispatch.sink_faulted',
  // plan 101 slice 05 D2 (owner ruling 2026-09-21: archive to R2, then prune past
  // a written window). `audit.archived` — one verified object (re-read and
  // checksummed) holding a keyset batch of `audit_events` / `task_run_events`
  // rows, written BEFORE the watermark that licenses their delete moves.
  // `audit.archive_pruned` — one row per pass of the delete that follows, on every
  // outcome, for the reason `inbound_events.pruned` gives: a missing row must read
  // as "the schedule stopped", never "nothing aged out".
  AuditArchived: 'audit.archived',
  AuditArchivePruned: 'audit.archive_pruned',
  // #5363: a `.maintainer.yml` `schedules:` entry came due and the hourly
  // `cron.schedules` lane (`apps/worker/src/cron/schedules.ts`) acted on it.
  //   `schedule.fired` — one `return_context` scout task per covered repo was
  //     minted (at least one fresh). Input `{ anchor, name, slot, brief_path,
  //     brief_sha, task_ids }`: the sha is the brief's blob at default-branch HEAD,
  //     so the checkup the scout ran is re-readable after the file moves on.
  //   `schedule.refused` — the slot was due and NOTHING was minted. Input
  //     `{ anchor, name, slot, kind, ... }`, `kind` naming why (`policy_unknown`,
  //     `brief_missing`, `brief_too_large`, `no_installation`, `no_scout_capacity`,
  //     `repo_not_enabled`, `repo_is_anchor`, `previous_in_flight` — the previous
  //     firing still has a task in motion), `repo` naming the listed repo on the
  //     two repo arms. A due checkup that silently did not run is the failure: the
  //     maintainer opted in and would read the silence as "all clear".
  // Tenant-anchored and `visibleToMaintainer`: both are about one account's repo.
  ScheduleFired: 'schedule.fired',
  ScheduleRefused: 'schedule.refused',
  // #7789, the security watch (`apps/worker/src/security-watch/`), all on the org's own chain:
  //   - `filed`: a first-tier advisory matched this repo. Carries running vs fixed and the
  //     urgency, so "a KEV ask jumped the queue" is on the record with why.
  //   - `raised`: a KEV listing landed on an ask already filed.
  //   - `deferred`: a person's open pull request in the same area parked an ordinary fix.
  //   - `patched`: a merged pull request closed it; the only way a row reads patched.
  SecurityWatchFiled: 'security_watch.filed',
  SecurityWatchRaised: 'security_watch.raised',
  SecurityWatchDeferred: 'security_watch.deferred',
  SecurityWatchPatched: 'security_watch.patched',
  // Plan 101 slice 05 (#5427): the intake screen read ONE staff bug report
  // (`platform_bugs`) on the worker's screening lane. EVERY attempt writes one — `ok`,
  // `discard` and `unavailable` (the classifier could not answer; the row stays
  // `screening` and `failures` counts toward the lane's escalation bound). Platform-
  // chain: the lane owns it, not a tenant — and it is HERE, not a
  // `platform_audit_events` row, because that table's `actor_user_id` names a person
  // and the screen is a machine act. Input `{ bugId }`; output `{ verdict, reason,
  // prompt_version, cached, failures }` — never the title or body.
  PlatformBugScreened: 'platform_bug.screened',
  // Plan 101 slice 07: the worker re-read ONE gitlab.com merge request named by a
  // connected `gitlab` webhook source's hint, through the GitLab REST client with the
  // source's own API token, and graded it. READ-ONLY: nothing was written back to
  // GitLab; this row is the whole effect. Tenant-anchored (`audit: { kind: 'tenant' }`,
  // the gateway session per account per month) and `visibleToMaintainer`. Output:
  // `ForgeMrObservedOutputSchema` (`./payload`) — path, iid, head sha, merge verdict,
  // CI verdict, approvals; never the token, never the delivery payload (that stays in
  // `inbound_events`).
  ForgeMrObserved: 'forge.mr.observed',
  // Plan 101 slice 07: the same lane did NOT observe the merge request a hint named,
  // for a reason a retry cannot change — the token was rejected (401) or lacks access
  // (403), the MR or project is gone (404), or the source is missing, disconnected,
  // re-pointed or holds no readable token. Without this row a maintainer whose token
  // expired sees observations simply stop. One row per refused hint, tenant-anchored and
  // `visibleToMaintainer`. Output: `ForgeMrObserveRefusedOutputSchema` (`./payload`) —
  // source id, project id, MR iid and the closed reason; never the token. A transient
  // failure (5xx, network, a 429 past the client's ladder) is NOT this row: it throws
  // for the lane's retry ladder.
  ForgeMrObserveRefused: 'forge.mr.observe_refused',
  // Plan 101 slice 10b: the Gitee twin of `forge.mr.observed`. The worker re-read ONE
  // gitee.com pull request named by a connected `gitee` webhook source's hint, through the
  // Gitee v5 client with the source's own personal access token. READ-ONLY: nothing was
  // written back to Gitee. Its own kind, not a widened `forge.mr.observed` (whose strict
  // output is GitLab-shaped). Tenant-anchored and `visibleToMaintainer`. Output:
  // `ForgePrObservedOutputSchema` (`./payload`) — `forge: 'gitee'`, path, number, head sha,
  // state / mergeable / draft, CI verdict, reviewer and tester accept counts; never the
  // token, never the delivery payload.
  ForgePrObserved: 'forge.pr.observed',
  // Plan 101 slice 10b: the same lane did NOT observe the PR a hint named, for a reason a
  // retry cannot change — the token was rejected (401) or lacks access (403), the repo is
  // gone (404 `Not Found Project`) or the PR is (404 `Pull Request not found`), or the
  // source is missing, disconnected, re-pointed or holds no readable token. One row per
  // refused hint, tenant-anchored and `visibleToMaintainer`. Output:
  // `ForgePrObserveRefusedOutputSchema` (`./payload`). A transient failure (5xx, network,
  // timeout, Gitee's 401-shaped rate limit) is NOT this row: it throws for the retry ladder.
  ForgePrObserveRefused: 'forge.pr.observe_refused',
} as const;
